import type { IConfigManifest, ITranslationStorage, LANGUAGE_CODE } from '@monofe/util';
import type { ICookieViewText } from './CookieView.interface';

/* Override the global window to access the doNotTrack property */
declare global {
    interface Window {
        doNotTrack: string | null | undefined;
    }
}

function convertToBoolean<T>(value: T): boolean {
    switch (typeof value) {
        case 'boolean':
            return value;

        case 'string':
            return (
                value.toLowerCase() === 'yes' ||
                value.toLowerCase() === 'on' ||
                value.toLowerCase() === '1' ||
                value.toLowerCase() === 'true'
            );

        case 'bigint':
        case 'number':
            return value !== 0;

        default:
            return false;
    }
}

/* Check if Do Not Track flag is set in browse to prevent analytics tracking */
const isDoNotTrackEnabled = () =>
    convertToBoolean(navigator.doNotTrack ?? window.doNotTrack ?? false);

export function canEnableAnalytics(): boolean {
    const isLocalHost = window.location.href.includes('localhost');
    return !isLocalHost && !isDoNotTrackEnabled();
}

export enum TranslationKey {
    COOKIE_HEADING_TEXT = 'Cookie consent',
    COOKIE_BODY_TEXT = 'We use cookies to understand your activities on %@ only. We take your privacy very seriously and use this information to serve you better. Please see our privacy policy for details and any questions.',
    COOKIE_ACCEPT_TEXT = 'Accept All',
    COOKIE_DECLINE_TEXT = 'Reject All',
    COOKIE_PRIVACY_TEXT = 'Privacy Policy',
}

export function generateTranslatedText(
    manifest: IConfigManifest,
    language: LANGUAGE_CODE,
    translater: ITranslationStorage,
) {
    const translate = (originalText: string, params: string[] = []) =>
        translater.translation(language, originalText, params, originalText);

    const translations: ICookieViewText = {
        headingText: translate(TranslationKey.COOKIE_HEADING_TEXT),
        bodyText: translate(TranslationKey.COOKIE_BODY_TEXT, [manifest.name]),
        acceptText: translate(TranslationKey.COOKIE_ACCEPT_TEXT),
        declineText: translate(TranslationKey.COOKIE_DECLINE_TEXT),
        privacyPolicyText: translate(TranslationKey.COOKIE_PRIVACY_TEXT),
    };

    return translations;
}
